/**
 * Mirror modifier — pure typed-array core (Three.js-free).
 *
 * Reflects geometry across one or more axes with optional vertex merging.
 */

import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import {
  composeProvenance,
  identityProvenance,
  provenanceFromSourceMap,
} from "../../modeling/provenance.ts";
import { buildTopologyWithExplicitEdges } from "../../modeling/topology.ts";
import {
  asSharedTriangleIndices,
  type PolygonTopology,
} from "../../modeling/types.ts";
import type { GeometryGroup, ModifierGeometry } from "./modifierTypes.ts";

export function mirrorCore(
  geo: ModifierGeometry,
  mod: Extract<BlendModifier, { kind: "mirror" }>,
): ModifierGeometry {
  const axes: number[] = [];
  if (mod.axisX) axes.push(0);
  if (mod.axisY) axes.push(1);
  if (mod.axisZ) axes.push(2);
  if (axes.length === 0) return geo;

  const origin = mod.mirrorOrigin ?? [0, 0, 0];
  // Compose per-axis step provenances so the final `current.provenance` maps
  // the INPUT `geo`'s vert space to the final (possibly multi-axis) output.
  // Each `mirrorOnAxisCore` writes its own input-to-output step provenance
  // on the returned geometry.
  let current = geo;
  let stepProv = identityProvenance(geo.positions.length / 3);
  for (const axis of axes) {
    current = mirrorOnAxisCore(current, axis, origin, mod.merge, mod.tolerance);
    stepProv = composeProvenance(stepProv, current.provenance);
  }
  return { ...current, provenance: stepProv };
}

function mirrorOnAxisCore(
  geo: ModifierGeometry,
  axis: number,
  origin: [number, number, number],
  merge: boolean,
  tolerance: number,
): ModifierGeometry {
  const positions = geo.positions;
  const normals = geo.normals;
  const vertCount = positions.length / 3;
  const center = origin[axis];

  const mirroredPos = new Float32Array(positions.length);
  const mirroredNorm = normals ? new Float32Array(normals.length) : null;

  for (let i = 0; i < vertCount; i++) {
    const base = i * 3;
    mirroredPos[base] = positions[base];
    mirroredPos[base + 1] = positions[base + 1];
    mirroredPos[base + 2] = positions[base + 2];
    mirroredPos[base + axis] = 2 * center - positions[base + axis];

    if (mirroredNorm && normals) {
      mirroredNorm[base] = normals[base];
      mirroredNorm[base + 1] = normals[base + 1];
      mirroredNorm[base + 2] = normals[base + 2];
      mirroredNorm[base + axis] = -normals[base + axis];
    }
  }

  const mirrorRemap = new Uint32Array(vertCount);
  let mergedCount = 0;

  if (merge && tolerance > 0) {
    for (let i = 0; i < vertCount; i++) {
      const base = i * 3;
      const dx = positions[base] - mirroredPos[base];
      const dy = positions[base + 1] - mirroredPos[base + 1];
      const dz = positions[base + 2] - mirroredPos[base + 2];
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (dist <= tolerance) {
        mirrorRemap[i] = i;
        mergedCount++;
      } else {
        mirrorRemap[i] = vertCount + i - mergedCount;
      }
    }
  } else {
    for (let i = 0; i < vertCount; i++) {
      mirrorRemap[i] = vertCount + i;
    }
  }

  const outVertCount = vertCount * 2 - mergedCount;
  const newPos = new Float32Array(outVertCount * 3);
  newPos.set(positions, 0);

  const newNorm =
    normals && mirroredNorm ? new Float32Array(outVertCount * 3) : null;
  if (newNorm && normals) newNorm.set(normals, 0);

  let writeIdx = vertCount;
  for (let i = 0; i < vertCount; i++) {
    if (mirrorRemap[i] === i) continue;
    const base = i * 3;
    const wBase = writeIdx * 3;
    newPos[wBase] = mirroredPos[base];
    newPos[wBase + 1] = mirroredPos[base + 1];
    newPos[wBase + 2] = mirroredPos[base + 2];
    if (newNorm && mirroredNorm) {
      newNorm[wBase] = mirroredNorm[base];
      newNorm[wBase + 1] = mirroredNorm[base + 1];
      newNorm[wBase + 2] = mirroredNorm[base + 2];
    }
    writeIdx++;
  }

  const uvs = geo.uvs;
  let newUVs: Float32Array | null = null;
  if (uvs) {
    newUVs = new Float32Array(outVertCount * 2);
    newUVs.set(uvs, 0);
    let uvWriteIdx = vertCount;
    for (let i = 0; i < vertCount; i++) {
      if (mirrorRemap[i] === i) continue;
      const uBase = i * 2;
      const wBase = uvWriteIdx * 2;
      newUVs[wBase] = uvs[uBase];
      newUVs[wBase + 1] = uvs[uBase + 1];
      uvWriteIdx++;
    }
  }

  let newIndices = geo.indices;
  const origIndices = geo.indices;
  if (origIndices.length > 0) {
    newIndices = asSharedTriangleIndices(
      new Uint32Array(origIndices.length * 2),
    );
    newIndices.set(origIndices, 0);
    for (let i = 0; i < origIndices.length; i += 3) {
      const base = origIndices.length + i;
      newIndices[base] = mirrorRemap[origIndices[i]];
      newIndices[base + 1] = mirrorRemap[origIndices[i + 2]]; // swap winding
      newIndices[base + 2] = mirrorRemap[origIndices[i + 1]];
    }
  }

  const newMorphArrays = geo.morphArrays.map((deltas) => {
    const out = new Float32Array(outVertCount * 3);
    out.set(deltas, 0);
    let mWriteIdx = vertCount;
    for (let i = 0; i < vertCount; i++) {
      if (mirrorRemap[i] === i) continue;
      const base = i * 3;
      const wBase = mWriteIdx * 3;
      out[wBase] = deltas[base];
      out[wBase + 1] = deltas[base + 1];
      out[wBase + 2] = deltas[base + 2];
      out[wBase + axis] = -deltas[base + axis];
      mWriteIdx++;
    }
    return out;
  });

  // Duplicate groups for mirrored half
  const newGroups: GeometryGroup[] = [];
  if (geo.groups.length > 0) {
    for (const g of geo.groups) {
      newGroups.push({
        start: g.start,
        count: g.count,
        materialIndex: g.materialIndex,
      });
    }
    const indexCount = origIndices.length;
    for (const g of geo.groups) {
      newGroups.push({
        start: g.start + indexCount,
        count: g.count,
        materialIndex: g.materialIndex,
      });
    }
  }

  // Propagate wire edges: remap each edge through mirrorRemap. Creases
  // travel alongside flags — both are per-edge attributes that a downstream
  // modifier (notably subsurf) reads in tandem.
  let newEdges: Uint32Array | null = null;
  let newEdgeFlags: Uint8Array | null = null;
  let newEdgeCreases: Float32Array | null = null;
  if (geo.edges && geo.edges.length > 0 && geo.edgeFlags) {
    const srcEdgeCount = geo.edges.length / 2;
    const srcCreases = geo.edgeCreases ?? null;
    // Original edges + mirrored edges (may include degenerate self-edges for merged verts)
    const outEdges: number[] = [];
    const outFlags: number[] = [];
    const outCreases: number[] | null = srcCreases ? [] : null;
    // Original edges pass through
    for (let i = 0; i < srcEdgeCount; i++) {
      outEdges.push(geo.edges[i * 2], geo.edges[i * 2 + 1]);
      outFlags.push(geo.edgeFlags[i]);
      if (outCreases && srcCreases) outCreases.push(srcCreases[i]);
    }
    // Mirrored edges
    for (let i = 0; i < srcEdgeCount; i++) {
      const ma = mirrorRemap[geo.edges[i * 2]];
      const mb = mirrorRemap[geo.edges[i * 2 + 1]];
      if (ma !== mb) {
        // skip degenerate (merged endpoints)
        outEdges.push(ma, mb);
        outFlags.push(geo.edgeFlags[i]);
        if (outCreases && srcCreases) outCreases.push(srcCreases[i]);
      }
    }
    newEdges = new Uint32Array(outEdges);
    newEdgeFlags = new Uint8Array(outFlags);
    if (outCreases) newEdgeCreases = new Float32Array(outCreases);
  }

  // Propagate skin radii: original verts keep their radius; mirrored verts
  // inherit the same radius as their source vertex (reflection preserves scale).
  let newSkinRadii: Float32Array | null = null;
  if (geo.skinRadii) {
    newSkinRadii = new Float32Array(outVertCount);
    newSkinRadii.set(geo.skinRadii, 0);
    let srWriteIdx = vertCount;
    for (let i = 0; i < vertCount; i++) {
      if (mirrorRemap[i] === i) continue; // merged — already written at index i
      newSkinRadii[srWriteIdx] = geo.skinRadii[i];
      srWriteIdx++;
    }
  }

  // Propagate polygon topology: original faces unchanged, mirrored faces use
  // reversed winding (first vertex stays, rest reversed) with vertices remapped
  // through mirrorRemap — matching the winding reversal applied to tri indices above.
  let newTopology: PolygonTopology | undefined;
  if (geo.topology) {
    const { offsets, corners } = geo.topology;
    const faceCount = offsets.length - 1;
    const srcCornerCount = corners.length;

    const newOffsets = new Uint32Array(faceCount * 2 + 1);
    const newCorners = new Uint32Array(srcCornerCount * 2);

    // Original faces pass through unchanged
    for (let f = 0; f <= faceCount; f++) newOffsets[f] = offsets[f];
    newCorners.set(corners, 0);

    // Mirrored faces: reversed winding, vertices remapped
    let cornerWrite = srcCornerCount;
    for (let f = 0; f < faceCount; f++) {
      const start = offsets[f];
      const size = offsets[f + 1] - start;
      // Keep first corner, reverse the rest — same pattern as tri winding swap.
      newCorners[cornerWrite] = mirrorRemap[corners[start]];
      for (let j = 1; j < size; j++) {
        newCorners[cornerWrite + j] = mirrorRemap[corners[start + size - j]];
      }
      cornerWrite += size;
      newOffsets[faceCount + f + 1] = cornerWrite;
    }

    newTopology = buildTopologyWithExplicitEdges(
      newOffsets,
      newCorners,
      newEdges ?? new Uint32Array(0),
      newEdgeFlags ?? new Uint8Array(0),
      newEdgeCreases,
    );
    newEdges = newTopology.edges;
    newEdgeFlags = newTopology.edgeFlags;
    newEdgeCreases = newTopology.edgeCreases ?? null;
  }

  // Provenance: source verts land at identity indices [0..vertCount); each
  // mirrored vert lives at `mirrorRemap[i]` and descends from source vert
  // `i`. `mirrorRemap[i] === i` means the vert was merged into its mirror,
  // so the output vert at `i` has the single source parent `i` either way.
  const srcForNew = new Int32Array(outVertCount);
  for (let i = 0; i < vertCount; i++) srcForNew[i] = i;
  for (let i = 0; i < vertCount; i++) {
    const outIdx = mirrorRemap[i];
    if (outIdx !== i) srcForNew[outIdx] = i;
  }
  const provenance = provenanceFromSourceMap(vertCount, srcForNew);

  return {
    positions: newPos,
    indices: newIndices,
    normals: newNorm,
    uvs: newUVs,
    morphArrays: newMorphArrays,
    skinIndex: geo.skinIndex,
    skinWeight: geo.skinWeight,
    sculptDisplacement: geo.sculptDisplacement,
    groups: newGroups,
    morphTargetsRelative: geo.morphTargetsRelative,
    morphNames: geo.morphNames,
    edges: newEdges,
    edgeFlags: newEdgeFlags,
    edgeCreases: newEdgeCreases,
    skinRadii: newSkinRadii,
    topology: newTopology,
    provenance,
  };
}
